import React, { useMemo } from 'react';
import { history, useIntl, useSelector, useDispatch, getLocale } from 'umi';
import { Card } from 'tea-component';
import Loading from '@intelligence/components/Loading';
import Swiper from '@intelligence/components/Swiper';
import TimelineContext from '@intelligence/pages/TopCharts/components/DetailsPage/TimelineContext';
import DownloadableContent from '@intelligence/pages/TopCharts/components/DetailsPage/DownloadableContent';
import Description from '@intelligence/pages/TopCharts/components/DetailsPage/Description';
import ReleaseDateByPlatform from '@intelligence/pages/TopCharts/components/DetailsPage/ReleaseDateByPlatform';
import TagsCard from '@intelligence/pages/TopCharts/components/DetailsPage/TagsCard';
import useFilterExtend from '@intelligence/hooks/useFilterExtend';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { GenreTagClickType, generateGenreSelectValueSimple } from '@intelligence/v3/utils/genre';
import { saveFilterName, formatGenerePageFunc } from '@intelligence/components/Filter/util';
import {
  getSensorTowerTagCategoryFilterKey,
  formatGenreTagsForTagCard,
  formatSensorTowerTagsForTagCard,
  formatAppMagicGenreTagsForTagCard,
} from '@intelligence/hooks/useSenTowerTag';
import { namespace as HOME_NAMESPACE } from '@home/models/home';
import { FILTER_EXTEND_NAMESPACE } from '@intelligence/models/filterExtend';
import * as filterKeys from '@intelligence/components/Filter/FilterBase/_config';
import {
  MObile_ENTITY_TYPE,
  PC_CONSOLE_COMBINE_ENTITY_TYPE,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { LANGUAGE_CN } from '@home/_config/config';
import LOCALE_EN from '@intelligence/_locales/en-US/common';
import LOCALE_CN from '@intelligence/_locales/zh-CN/common';
import GameInfoTagCard from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/GameInfoTagCard';
import useFilterTagsOptions from '@intelligence/hooks/useFilterTagsOptions';

import type { HomeModelStateType } from '@home/models/home';
import type { ICommonOptionCategory } from '@intelligence/types/common';

import './index.scss';

// Sensor Tower Tags模块 category的别名映射，优先展示该映射中的别名，其次再展示接口返回的name
const TAGS_CATEGORIES_ALIAS = {
  Tag: 'Tags',
};

interface ITagCardTitleProps extends IBaseProps {
  dataSource: string;
  title: string;
}

interface ITagTranslateMap {
  [key: string]: {
    tag_cn: string;
    tag_en: string;
  };
}

const generateTags = (isCn?: boolean, translateMap?: ITagTranslateMap, tagString?: string): ICommonOptionCategory[] => {
  if (!tagString) {
    return [];
  }

  return tagString.split('|').map((tag) => {
    const translation = translateMap?.[tag?.toLowerCase()];
    const tagCN = translation?.tag_cn || tag;
    const tagEN = translation?.tag_en || tag;
    return {
      value: tag,
      text: isCn ? tagCN : tagEN,
      textEN: tagEN,
      textCN: tagCN,
    };
  });
};

const TagCardTitle: React.FC<ITagCardTitleProps> = (props) => {
  const { dataSource, title } = props;

  return (
    <div className="dbi-sensor-tower-tags-card__title">
      <img className="dbi-sensor-tower-tags-card__title-icon" src={getDataSourceIcon(dataSource)} alt="" />
      <span className="dbi-sensor-tower-tags-card__title-text">{title}</span>
    </div>
  );
};

export default function AboutGame(props: any) {
  const {
    isRelease,
    images,
    videos,
    swiperParams,
    desc,
    timelines,
    videoScreen,
    dLoading,
    entityType,
    downloadContent,
    releaseDateByPlatforms,
    sensorTowerTags,
    definitionsLoading,
    genreOptionsMap,
    filterTags,
    gameDetails,
    mainGenre,
    subGenre,
    ieggMainGenre,
    ieggSubGenre,
    gameEngineTags = [],
  } = props;

  const locale = getLocale();
  const isCn = locale === LANGUAGE_CN;
  // 中英切换方法
  const { formatMessage } = useIntl();

  const dispatch = useDispatch();
  const { userInfo } = useSelector<any, HomeModelStateType>((state) => state?.[HOME_NAMESPACE] || {});

  const { filterExtend, setFilterExtend } = useFilterExtend({});

  // 构建 app magic tags 翻译映射表
  const appMagicTagsTranslateMap = useMemo<ITagTranslateMap>(() => {
    if (!filterTags) return {};

    const translateMap: ITagTranslateMap = {};

    // 遍历所有 app magic tag 类型
    Object.values(filterKeys.APPMAGIC_TAG_MAP).forEach((apiFieldName) => {
      const tagList = filterTags[apiFieldName];
      if (Array.isArray(tagList) && tagList.length > 0) {
        tagList.forEach((item: { tag: string; tag_cn: string; tag_en: string }) => {
          const itemTag = item?.tag || '';
          if (itemTag) {
            translateMap[itemTag.toLowerCase()] = {
              tag_cn: item.tag_cn || itemTag,
              tag_en: item.tag_en || itemTag,
            };
          }
        });
      }
    });

    return translateMap;
  }, [filterTags]);

  // Game Engine locale text map
  const { gameEngineLocaleTextMap } = useFilterTagsOptions();

  const noDataTip = useMemo<string>(() => {
    const emptyTipsLocaleKey = isRelease ? 'dbi_detail_common_nodata_tips' : 'dbi_detail_game_not_launch_tips';
    return formatMessage({ id: emptyTipsLocaleKey });
  }, [locale, isRelease]);

  const descNoDataTip = useMemo<string>(() => {
    const emptyTipsLocaleKey = isRelease ? 'dbi_detail_description_nodata_tips' : 'dbi_detail_game_not_launch_tips';
    return formatMessage({ id: emptyTipsLocaleKey });
  }, [locale, isRelease]);

  const sensorTowerTagsForTagCard = useMemo<ICommonOptionCategory[]>(() => {
    if (entityType !== 'mobile') return [];
    const tags1 = formatGenreTagsForTagCard({
      definitionsLoading,
      genreOptions: genreOptionsMap?.sensortower,
      superGenreString: gameDetails?.sensortower_genre,
      majorGenreString: gameDetails?.sensortower_sub_genre,
    });
    const tags2 = formatSensorTowerTagsForTagCard(sensorTowerTags);

    return [...tags1, ...tags2];
  }, [entityType, definitionsLoading, genreOptionsMap, mainGenre, subGenre, sensorTowerTags]);

  const ieggGenreTagsForCard = useMemo<ICommonOptionCategory[]>(() => {
    if (entityType !== 'mobile') {
      return [];
    }

    return formatGenreTagsForTagCard({
      definitionsLoading,
      genreOptions: genreOptionsMap?.iegg,
      superGenreString: ieggMainGenre,
      majorGenreString: ieggSubGenre,
    });
  }, [definitionsLoading, entityType, genreOptionsMap, ieggMainGenre, ieggSubGenre]);

  const appMagicTagsForCard = useMemo<ICommonOptionCategory[]>(() => {
    if (entityType !== 'mobile') {
      return [];
    }

    const appMagicGenreOptions = genreOptionsMap?.appmagic || genreOptionsMap?.app_magic;
    const tags1 = formatAppMagicGenreTagsForTagCard({
      definitionsLoading,
      genreOptions: appMagicGenreOptions,
      superGenreString: gameDetails?.super_genre,
      majorGenreString: gameDetails?.major_category,
      midGenreString: gameDetails?.mid_category,
      subGenreString: gameDetails?.sub_category,
      minorGenreString: gameDetails?.minor_category,
    });

    const gameDetailsExtJSON = gameDetails?.ext_json || {};
    const appMagicTagData = gameDetailsExtJSON?.app_magic || {};

    const tags2: ICommonOptionCategory[] = filterKeys.APPMAGIC_TAG_KEYS.map((appMagicTagKey) => {
      const nameLocaleKey = filterKeys.APPMAGIC_TAG_TITLE_MAP[appMagicTagKey];
      const valueKey =
        appMagicTagKey === filterKeys.APPMAGIC_TAG_ART_STYLES
          ? 'artstyles'
          : filterKeys.APPMAGIC_TAG_MAP[appMagicTagKey];
      return {
        value: appMagicTagKey,
        // @ts-ignore
        text: LOCALE_EN[nameLocaleKey],
        // @ts-ignore
        textEN: LOCALE_EN[nameLocaleKey],
        // @ts-ignore
        textCN: LOCALE_CN[nameLocaleKey],
        children: generateTags(isCn, appMagicTagsTranslateMap, appMagicTagData?.[valueKey]),
      };
    });

    return [...tags1, ...tags2];
  }, [isCn, entityType, definitionsLoading, genreOptionsMap, gameDetails, appMagicTagsTranslateMap]);

  const getRightTitle = () => {
    switch (entityType) {
      case 'mobile':
        return formatMessage({ id: `dbi_timeline` });
      case 'pc':
      case PC_CONSOLE_COMBINE_ENTITY_TYPE:
        return formatMessage({ id: `dbi_downloadable_content` });
      case 'console':
        return formatMessage({ id: `dbi_description` });
      default:
        return '';
    }
  };

  const getRightComponent = () => {
    switch (entityType) {
      case 'mobile':
        return <TimelineContext timelines={timelines} noDataTip={noDataTip} />;
      case 'pc':
      case PC_CONSOLE_COMBINE_ENTITY_TYPE:
        return <DownloadableContent isRelease={isRelease} entityType={entityType} downloadContent={downloadContent} />;
      case 'console':
        return <Description desc={desc} noDataTip={descNoDataTip} />;
      default:
        return null;
    }
  };

  const getTagKeyAndTagKeys = (type: 'sensortower' | 'appmagic' | 'iegg', key: string) => {
    let categoryTagKey = '';
    let categoryTagKeys: string[] = [];

    switch (type) {
      case 'sensortower': {
        categoryTagKey = getSensorTowerTagCategoryFilterKey(key);
        categoryTagKeys = filterKeys.SENSOR_TOWER_TAG_KEYS;
        break;
      }
      case 'appmagic': {
        categoryTagKey = key;
        categoryTagKeys = filterKeys.APPMAGIC_TAG_KEYS;
        break;
      }
      case 'iegg': {
        break;
      }
    }

    return { categoryTagKey, categoryTagKeys };
  };

  const generateGenreSelectValueOfSensortower = (categoryValue: string, tag: ICommonOptionCategory) => {
    const tagValue = tag?.value || '';

    switch (categoryValue) {
      case GenreTagClickType.SuperGenre: {
        return generateGenreSelectValueSimple(genreOptionsMap, 'sensortower', tagValue);
      }
      case GenreTagClickType.MajorGenre: {
        return generateGenreSelectValueSimple(genreOptionsMap, 'sensortower', tag?.parentValue || '', tagValue);
      }
    }
  };

  const generateGenreSelectValueOfIegg = (categoryValue: string, tag: ICommonOptionCategory) => {
    const tagValue = tag?.value || '';

    switch (categoryValue) {
      case GenreTagClickType.SuperGenre: {
        return generateGenreSelectValueSimple(genreOptionsMap, 'iegg', tagValue);
      }
      case GenreTagClickType.MajorGenre: {
        return generateGenreSelectValueSimple(genreOptionsMap, 'iegg', tag?.parentValue || '', tagValue);
      }
    }
  };

  const generateGenreSelectValueOfAppMagic = (categoryValue: string, tag: ICommonOptionCategory) => {
    const tagValue = tag?.value || '';
    const superGenre = gameDetails?.super_genre || '';
    const majorGenre = gameDetails?.major_category || '';
    const midGenre = gameDetails?.mid_category || '';
    const subGenre = gameDetails?.sub_category || '';

    switch (categoryValue) {
      case GenreTagClickType.SuperGenre: {
        return generateGenreSelectValueSimple(genreOptionsMap, 'appmagic', tagValue);
      }
      case GenreTagClickType.MajorGenre: {
        return generateGenreSelectValueSimple(genreOptionsMap, 'appmagic', superGenre, tagValue);
      }
      case GenreTagClickType.MidGenre: {
        return generateGenreSelectValueSimple(genreOptionsMap, 'appmagic', superGenre, majorGenre, tagValue);
      }
      case GenreTagClickType.SubGenre: {
        return generateGenreSelectValueSimple(genreOptionsMap, 'appmagic', superGenre, majorGenre, midGenre, tagValue);
      }
      case GenreTagClickType.MinorGenre: {
        return generateGenreSelectValueSimple(
          genreOptionsMap,
          'appmagic',
          superGenre,
          majorGenre,
          midGenre,
          tag?.parentValue || subGenre,
          tagValue,
        );
      }
    }
  };

  const generateGenreSelectValueByType = (
    type: 'sensortower' | 'appmagic' | 'iegg',
    categoryValue: string,
    tag: ICommonOptionCategory,
  ) => {
    switch (type) {
      case 'sensortower': {
        return generateGenreSelectValueOfSensortower(categoryValue, tag);
      }
      case 'iegg': {
        return generateGenreSelectValueOfIegg(categoryValue, tag);
      }
      case 'appmagic': {
        return generateGenreSelectValueOfAppMagic(categoryValue, tag);
      }
      default: {
        return null;
      }
    }
  };

  const handleGenreTagsClick = (
    type: 'sensortower' | 'appmagic' | 'iegg',
    categoryValue: string,
    tag: ICommonOptionCategory,
  ) => {
    const genreValue = generateGenreSelectValueByType(type, categoryValue, tag);

    if (!genreValue) {
      return;
    }

    const options = genreOptionsMap?.[type];

    setFilterExtend({
      ...filterExtend,
      [filterKeys.GENRETOR]: formatGenerePageFunc({ ...genreValue, options }, '', {
        isStdGenreValuesHandler: true,
      }),
    });
    saveFilterName(userInfo as any, '');
    history.push('/intelligence/topCharts/mobileGames');
  };

  const handleTagsClick = (type: 'sensortower' | 'appmagic' | 'iegg', categoryValue: string, tag: ICommonOptionCategory) => {
    if (
      [
        GenreTagClickType.SuperGenre,
        GenreTagClickType.MajorGenre,
        GenreTagClickType.MidGenre,
        GenreTagClickType.SubGenre,
        GenreTagClickType.MinorGenre,
      ].includes(categoryValue as unknown as GenreTagClickType)
    ) {
      handleGenreTagsClick(type, categoryValue, tag);
      return;
    }

    const { categoryTagKey, categoryTagKeys } = getTagKeyAndTagKeys(type, categoryValue);
    const value = tag?.value || '';

    const categoryTagExtendValue = categoryTagKeys.reduce((current, next) => {
      const nextValue = next !== categoryTagKey ? [] : [value];

      return {
        ...current,
        [next]: {
          value: nextValue,
        },
      };
    }, {});

    setFilterExtend({
      ...filterExtend,
      ...categoryTagExtendValue,
    });
    saveFilterName(userInfo as any, '');
    dispatch({
      type: `${FILTER_EXTEND_NAMESPACE}/saveIsClickSensorTowTag`,
      payload: true,
    });

    history.push('/intelligence/topCharts/mobileGames');
  };

  return (
    <Card className="dbi-about-game">
      <Card.Body title={formatMessage({ id: `dbi_about_the_game` })}>
        {dLoading ? (
          <>
            <div style={{ height: 200, color: '#444' }}>
              <Loading />
            </div>
          </>
        ) : (
          <div className="dbi-about-flex">
            <div
              className={`dbi-about-flex-left ${
                entityType === 'pc' || entityType === 'combine' ? 'dbi-about-flex-col' : ''
              }`}
            >
              <div className="dbi-video-box">
                <div className="dbi-about-title">{formatMessage({ id: `dbi_screenshots` })}</div>
                <Swiper
                  height={'calc(100% - 31px)'}
                  urlList={images}
                  videoScreen={videoScreen}
                  videos={videos}
                  noDataTip={noDataTip}
                  params={swiperParams}
                />
              </div>
              {entityType !== 'console' && (
                <div className="text-box dbi-about-flex-col-auto">
                  <div className="dbi-about-title">{formatMessage({ id: `dbi_description` })}</div>
                  <div className="text-box-context">
                    {desc ? (
                      <div className="intelligence-desc" dangerouslySetInnerHTML={{ __html: desc }}></div>
                    ) : (
                      <div className="nodata-center-des">{descNoDataTip}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div
              className={`dbi-about-flex-right ${entityType === 'pc' ? 'dbi-about-flex-auto-height' : ''} ${
                entityType === 'combine' ? 'dbi-about-flex-combine-height' : ''
              }`}
            >
              {entityType === MObile_ENTITY_TYPE && (
                <>
                  <TagsCard
                    className="dbi-sensor-tower-tags-card dbi-sensor-tower-tags-card--auto-height"
                    title={<TagCardTitle dataSource="iegg" title={formatMessage({ id: 'dbi_top_iegg_genre' })} />}
                    noDataTip={noDataTip}
                    list={ieggGenreTagsForCard}
                    onClick={(categoryValue, tag) => {
                      handleTagsClick('iegg', categoryValue, tag);
                    }}
                  />
                  <TagsCard
                    className="dbi-sensor-tower-tags-card"
                    title={
                      <TagCardTitle
                        dataSource="sensortower"
                        title={formatMessage({ id: 'dbi_detail_sensor_tower_tag' })}
                      />
                    }
                    noDataTip={noDataTip}
                    categoryAlias={TAGS_CATEGORIES_ALIAS}
                    list={sensorTowerTagsForTagCard}
                    onClick={(categoryValue, tag) => {
                      handleTagsClick('sensortower', categoryValue, tag);
                    }}
                  />
                  <TagsCard
                    className="dbi-sensor-tower-tags-card"
                    title={
                      <TagCardTitle dataSource="appmagic" title={formatMessage({ id: 'dbi_detail_appmagic_tag' })} />
                    }
                    noDataTip={noDataTip}
                    categoryAlias={TAGS_CATEGORIES_ALIAS}
                    list={appMagicTagsForCard}
                    onClick={(categoryValue, tag) => {
                      handleTagsClick('appmagic', categoryValue, tag);
                    }}
                  />
                </>
              )}
              {/* Game Engine 游戏引擎 */}
              {entityType === MObile_ENTITY_TYPE && (
                <GameInfoTagCard
                  type={filterKeys.GAME_ENGINE}
                  title={formatMessage({ id: 'dbi_top_game_engine' })}
                  tagList={gameEngineTags}
                  localeTextMap={gameEngineLocaleTextMap}
                  entityType={entityType}
                />
              )}
              <div className="dbi-about-flex-right-box">
                <div className="dbi-about-title">{getRightTitle()}</div>
                {getRightComponent()}
              </div>
              {entityType === PC_CONSOLE_COMBINE_ENTITY_TYPE && (
                <ReleaseDateByPlatform isRelease={isRelease} list={releaseDateByPlatforms} />
              )}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
